import { queryOptions, useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';

import { createProgressToast } from '@/components/common/progress-toast';
import { getAccessToken } from '@/lib/tokenStorage';
import {
  FertilizationQCResponse,
  ShipGroupListResponse,
  TOA_GetFreshShipmentFertList,
  TOA_GetFreshShipmentShipGroupMasters,
} from '@/rtk-query.gql';

import {
  isAbortError,
  ProgressStreamError,
  readProgressStream,
} from '../progress-stream';

import {
  CombineShipmentsRequest,
  EquipmentItem,
  FreshShipmentListItem,
  FreshStrawFertilizationV2,
  MatingLabelsRequest,
  MutationResult,
  SendCommDairyEmailRequest,
  SignOffRequest,
  UpdateDSFieldRequest,
  UpdateFertFieldRequest,
  UpdateFieldRequest,
  UpdateIncubatorAssignmentRequest,
  UpdateQcRequest,
} from './types';

export type { UpdateQcRequest as UpdateQcFreshShipmentInput } from './types';
export type { FreshStrawFertilizationV2 } from './types';

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(): HeadersInit {
  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function fetchGet<T>(path: string, params?: URLSearchParams): Promise<T> {
  const url = params
    ? `${getBaseUrl()}/api/v1/FreshShipment/${path}?${params.toString()}`
    : `${getBaseUrl()}/api/v1/FreshShipment/${path}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });

  if (response.status === 204) return [] as unknown as T;
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return (await response.json()) as T;
}

async function fetchFreshStrawFertilizationsV2(params: {
  fertilizationDate: string;
  fertilizationSite: string;
  freshShipmentId: string;
}): Promise<FreshStrawFertilizationV2[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('fertilizationDate', params.fertilizationDate);
  if (params.fertilizationSite)
    searchParams.set('fertilizationSite', params.fertilizationSite);
  if (params.freshShipmentId)
    searchParams.set('freshShipmentId', params.freshShipmentId);

  const url = `${getBaseUrl()}/api/v2/fresh-straw/fertilizations?${searchParams.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });

  if (response.status === 204) return [];
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return (await response.json()) as FreshStrawFertilizationV2[];
}

async function fetchBlob(
  path: string,
  params?: URLSearchParams,
): Promise<Blob> {
  const url = params
    ? `${getBaseUrl()}/api/v1/FreshShipment/${path}?${params.toString()}`
    : `${getBaseUrl()}/api/v1/FreshShipment/${path}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });

  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.blob();
}

async function fetchBlobReport(path: string, body?: unknown): Promise<Blob> {
  const url = `${getBaseUrl()}/api/v1/reports/${path}`;

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: getHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.blob();
}

async function fetchMutation<T>(
  path: string,
  method: 'POST' | 'PUT',
  body?: unknown,
): Promise<T> {
  const url = `${getBaseUrl()}/api/v1/FreshShipment/${path}`;

  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: getHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return (await response.json()) as T;
}

async function fetchShipGroupList(params: {
  fertilizationDate: string;
  fertilizationSiteId: string;
}): Promise<ShipGroupListResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('fertilizationDate', params.fertilizationDate);
  searchParams.set('fertilizationSiteId', params.fertilizationSiteId);
  return fetchGet<ShipGroupListResponse>('ship-group-list', searchParams);
}

async function fetchFreshShipmentList(params: {
  fertilizationDate: string;
  fertilizationSiteId: string;
  fertilizationSiteName: string;
}): Promise<FreshShipmentListItem[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('fertilizationDate', params.fertilizationDate);
  searchParams.set('fertilizationSiteId', params.fertilizationSiteId);
  searchParams.set('fertilizationSiteName', params.fertilizationSiteName);
  return fetchGet<FreshShipmentListItem[]>('fresh-shipment-list', searchParams);
}

async function fetchFertilizationList(params: {
  fertilizationDate: string;
  fertilizationSiteId: string;
  shipGroupId: string;
}): Promise<TOA_GetFreshShipmentFertList[]> {
  const searchParams = new URLSearchParams(params);
  return fetchGet<TOA_GetFreshShipmentFertList[]>(
    'fertilization-list',
    searchParams,
  );
}

async function fetchFreshShipmentQC(params: {
  fertilizationDate: string;
  fertilizationSiteId: string;
  shipGroupIds: string[];
}): Promise<FertilizationQCResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('fertilizationDate', params.fertilizationDate);
  searchParams.set('fertilizationSiteId', params.fertilizationSiteId);
  for (const id of params.shipGroupIds) {
    searchParams.append('shipGroupIds', id);
  }
  return fetchGet<FertilizationQCResponse>('qc', searchParams);
}

async function fetchEstimationHeaderVal(shipGroupId: string): Promise<number> {
  return fetchGet<number>(`estimation-header/${shipGroupId}`);
}

export function fertPredictionTechQueryOptions(fertilizationId: string) {
  return queryOptions({
    queryKey: ['fresh-shipment', 'fert-prediction-tech', fertilizationId],
    async queryFn() {
      const url = `${getBaseUrl()}/api/v1/FreshShipment/fert-prediction-tech/${fertilizationId}`;
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: getHeaders(),
      });
      if (response.status === 204) return null;
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      return (await response.json()) as EquipmentItem;
    },
  });
}

async function fetchShipmentGroupMasters(params: {
  fertilizationSiteId: string;
  shipGroupId: string;
}): Promise<TOA_GetFreshShipmentShipGroupMasters[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('fertilizationSiteId', params.fertilizationSiteId);
  searchParams.set('shipGroupId', params.shipGroupId);
  return fetchGet<TOA_GetFreshShipmentShipGroupMasters[]>(
    'ship-group-masters',
    searchParams,
  );
}

export const shipGroupListOptions = (params: {
  fertilizationDate: string;
  fertilizationSiteId: string;
}) =>
  queryOptions({
    queryKey: ['fresh-shipment', 'ship-group-list', params],
    async queryFn() {
      return fetchShipGroupList(params);
    },
  });

export const freshShipmentListOptions = (params: {
  fertilizationDate: string;
  fertilizationSiteId: string;
  fertilizationSiteName: string;
}) =>
  queryOptions({
    queryKey: ['fresh-shipment', 'fresh-shipment-list', params],
    async queryFn() {
      return fetchFreshShipmentList(params);
    },
  });

export type FreshShipmentFertListParams = {
  fertilizationDate: string;
  fertilizationSiteId: string;
  shipGroupId: string;
};

export const fertilizationListOptions = (params: FreshShipmentFertListParams) =>
  queryOptions({
    queryKey: ['fresh-shipment', 'fertilization-list', params],
    async queryFn() {
      return fetchFertilizationList(params);
    },
  });

export const freshShipmentFertListQueryOptions = fertilizationListOptions;

export const freshShipmentQCOptions = (params: {
  fertilizationDate: string;
  fertilizationSiteId: string;
  shipGroupIds: string[];
}) =>
  queryOptions({
    queryKey: ['fresh-shipment', 'qc', params],
    async queryFn() {
      return fetchFreshShipmentQC(params);
    },
  });

export const estimationHeaderValOptions = (shipGroupId: string) =>
  queryOptions({
    queryKey: ['fresh-shipment', 'estimation-header', shipGroupId],
    async queryFn() {
      return fetchEstimationHeaderVal(shipGroupId);
    },
  });

export const shipmentGroupMastersOptions = (params: {
  fertilizationSiteId: string;
  shipGroupId: string;
}) =>
  queryOptions({
    queryKey: ['fresh-shipment', 'ship-group-masters', params],
    async queryFn() {
      return fetchShipmentGroupMasters(params);
    },
  });

export const freshStrawFertilizationsV2Options = (params: {
  fertilizationDate: string;
  fertilizationSite: string;
  freshShipmentId: string;
}) =>
  queryOptions({
    queryKey: ['fresh-straw', 'fertilizations', 'v2', params],
    async queryFn() {
      return fetchFreshStrawFertilizationsV2(params);
    },
  });

export function useUpdateQcFreshShipmentRestMutation() {
  return useMutation({
    mutationFn: (input: UpdateQcRequest) =>
      fetchMutation<MutationResult>('update-qc', 'POST', input),
  });
}

export const useUpdateQcFreshShipmentRest =
  useUpdateQcFreshShipmentRestMutation;

export function useUpdateFertTransactionRestMutation() {
  return useMutation({
    mutationFn: (params: UpdateFertFieldRequest) =>
      fetchMutation<MutationResult>(`fertilization/${params.id}`, 'PUT', {
        field: params.field,
        value: params.value,
        updatePredictedComments: params.updatePredictedComments,
      }),
  });
}

export function useUpdateDSTransactionRestMutation() {
  return useMutation({
    mutationFn: (params: UpdateDSFieldRequest) =>
      fetchMutation<MutationResult>(`donor-schedule/${params.id}`, 'PUT', {
        field: params.field,
        value: params.value,
        updatePredictedComments: params.updatePredictedComments,
        isIvf: params.isIvf,
      }),
  });
}

export function useUpdateFreshShipmentTransactionRestMutation() {
  return useMutation({
    mutationFn: (params: UpdateFieldRequest) =>
      fetchMutation<MutationResult>(`fresh-shipment/${params.id}`, 'PUT', {
        field: params.field,
        value: params.value,
      }),
  });
}

export function useUpdateShipmentGroupTransactionRestMutation() {
  return useMutation({
    mutationFn: (params: UpdateFieldRequest) =>
      fetchMutation<MutationResult>(`shipment-group/${params.id}`, 'PUT', {
        field: params.field,
        value: params.value,
      }),
  });
}

export function useUpdateIncubatorAssignmentRestMutation() {
  return useMutation({
    mutationFn: (params: UpdateIncubatorAssignmentRequest) =>
      fetchMutation<MutationResult>(
        `incubator-assignment/${params.fertilizationId}`,
        'PUT',
        { shipGroupMasterId: params.shipGroupMasterId },
      ),
  });
}

export function useSignOffRestMutation() {
  return useMutation({
    mutationFn: (params: SignOffRequest) =>
      fetchMutation<MutationResult>('sign-off-email', 'POST', params),
  });
}

export function useUndoShipmentRestMutation() {
  return useMutation({
    mutationFn: (freshShipmentId: string) =>
      fetchMutation<MutationResult>(`undo-shipment/${freshShipmentId}`, 'POST'),
  });
}

export function useCombineShipmentsRestMutation() {
  return useMutation({
    mutationFn: (params: CombineShipmentsRequest) =>
      fetchMutation<MutationResult>('combine-shipments', 'POST', params),
  });
}

export function useCreateCommDairyExcelRestMutation() {
  return useMutation({
    mutationFn: (params: {
      fertilizationDate: string;
      fertilizationSiteId: string;
      shipGroupId: string;
      d5: boolean;
    }) => {
      const searchParams = new URLSearchParams();
      searchParams.set('fertilizationDate', params.fertilizationDate);
      searchParams.set('fertilizationSiteId', params.fertilizationSiteId);
      searchParams.set('shipGroupId', params.shipGroupId);
      searchParams.set('d5', String(params.d5));
      return fetchBlob('comm-dairy-excel', searchParams);
    },
  });
}

export function useSendCommDairyEmailRestMutation() {
  return useMutation({
    mutationFn: (params: SendCommDairyEmailRequest) =>
      fetchMutation<{ success: boolean }>('comm-dairy-email', 'POST', params),
  });
}

export function useMatingLabelsRestMutation() {
  return useMutation({
    mutationFn: (params: MatingLabelsRequest) => {
      const request = {
        fertilizationDate: format(
          new Date(params.fertilizationDate),
          'yyyy-MM-dd',
        ),
        site: params.site,
        shipGroups: params.shipGroups,
      };
      return fetchBlobReport('mating-labels', request);
    },
  });
}

export function useMatingLabelsDupesRestMutation() {
  return useMutation({
    mutationFn: (params: MatingLabelsRequest) => {
      const request = {
        fertilizationDate: format(
          new Date(params.fertilizationDate),
          'yyyy-MM-dd',
        ),
        site: params.site,
        shipGroups: params.shipGroups,
      };
      return fetchBlobReport('mating-labels-dupes', request);
    },
  });
}

export function useVerifyFreshShipmentMutation() {
  return useMutation({
    mutationFn: async (params: {
      freshShipmentId: string;
      verified: boolean;
    }) => {
      const url = `${getBaseUrl()}/api/fresh-straw/fresh-shipments/${params.freshShipmentId}/verify`;
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: getHeaders(),
        body: JSON.stringify({ verified: params.verified }),
      });
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      return (await response.json()) as { success: boolean };
    },
  });
}

export const shipmentEstimationOptions = (freshShipmentId: string) =>
  queryOptions({
    queryKey: ['fresh-shipment', 'shipment-estimation', freshShipmentId],
    async queryFn() {
      return fetchGet<number | null>(`shipment-estimations/${freshShipmentId}`);
    },
  });

export type FreshShipmentEmbryoImportResponse = {
  parsed: number;
  saved: number;
  skipped: number;
  updated: number;
  errors: string[];
};

export type FreshShipmentEmbryoImportArgs = {
  file: File;
};

async function getImportErrorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { message?: unknown };
    if (typeof body.message === 'string') {
      return body.message;
    }
  } catch {
    // Fall back to the status when the response has no JSON error body.
  }

  return `HTTP error! status: ${response.status}`;
}

export function useImportFreshEmbryosBatchV2Mutation() {
  return useMutation({
    mutationFn: async ({
      file,
    }: FreshShipmentEmbryoImportArgs): Promise<FreshShipmentEmbryoImportResponse> => {
      const token = getAccessToken();
      if (!token) throw new Error('No access token available. Please log in.');

      const formData = new FormData();
      formData.append('file', file);

      const url = `${getBaseUrl()}/api/fresh-straw/imports/fresh-embryos/batch/v2`;

      try {
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
          signal: AbortSignal.timeout(300_000),
        });

        if (!response.ok) {
          const text = await response.text();
          const err = new Error(text) as Error & { data: string };
          err.data = text;
          throw err;
        }
        return (await response.json()) as FreshShipmentEmbryoImportResponse;
      } catch (error: unknown) {
        if (error instanceof DOMException && error.name === 'TimeoutError') {
          const err = new Error(
            'Upload timed out after 5 minutes. The file may be too large — try splitting it into smaller batches.',
          ) as Error & { data: string };
          err.data =
            'Upload timed out after 5 minutes. The file may be too large — try splitting it into smaller batches.';
          throw err;
        }
        throw error;
      }
    },
  });
}

/**
 * Streaming fresh-embryo upload (batch/v3).
 *
 * The v2 endpoint returns one JSON body at the very end, so the browser sits on an idle
 * socket for the whole import and `AbortSignal.timeout` eventually kills it — the endless
 * spinner. v3 returns an NDJSON progress stream instead: bytes arrive immediately and at
 * least every 10 seconds, so nothing in the chain treats the connection as dead.
 *
 * Cancellation is an explicit AbortController wired to the toast's Cancel button rather
 * than a flat timeout, so a genuinely long-but-healthy import is never cut off.
 */
export function useImportFreshEmbryosBatchV3Mutation() {
  return useMutation({
    mutationFn: async ({
      file,
    }: FreshShipmentEmbryoImportArgs): Promise<FreshShipmentEmbryoImportResponse> => {
      const token = getAccessToken();
      if (!token) throw new Error('No access token available. Please log in.');

      const formData = new FormData();
      formData.append('file', file);

      const abortController = new AbortController();
      const progressToast = createProgressToast({
        title: 'Uploading fresh embryos',
        label: 'Processing rows',
        errorTitle: 'Upload failed',
        onCancel: () => abortController.abort(),
      });

      try {
        const response = await fetch(
          `${getBaseUrl()}/api/fresh-straw/imports/fresh-embryos/batch/v3`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
            signal: abortController.signal,
          },
        );

        if (!response.ok) {
          throw new Error(await getImportErrorMessage(response));
        }

        for await (const event of readProgressStream<
          FreshShipmentEmbryoImportResponse,
          { message: string }
        >(response)) {
          switch (event.type) {
            case 'progress':
              progressToast.report(event.progress);
              break;
            case 'success':
              progressToast.close();
              return event.success;
            case 'error':
              throw new ProgressStreamError(event.error);
          }
        }

        throw new Error('Upload stream ended without a terminal event.');
      } catch (error) {
        if (isAbortError(error)) {
          progressToast.cancelled();
          throw error;
        }

        const message =
          error instanceof ProgressStreamError
            ? (error.error as { message: string }).message
            : error instanceof Error
              ? error.message
              : 'Upload failed.';

        // The upload dialog owns error presentation (it also renders the per-row error
        // list), so just retire the progress toast here rather than showing a second one.
        progressToast.close();

        const err = new Error(message) as Error & { data: string };
        err.data = message;
        throw err;
      }
    },
  });
}

export function useAutoVerifyIfReconciledMutation() {
  return useMutation({
    mutationFn: async (
      fertilizationId: string,
    ): Promise<{ verified: boolean }> => {
      const url = `${getBaseUrl()}/api/fresh-straw/fertilizations/${fertilizationId}/auto-verify-if-reconciled`;
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: getHeaders(),
      });
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      return (await response.json()) as { verified: boolean };
    },
  });
}
